import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { TabContext, TabPanel } from '@mui/lab'
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { DatePicker } from '@mui/x-date-pickers'
import { format, isValid } from 'date-fns'
import { useFormik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import {
    AddCandidate,
    GetCandidateEducationOptions,
} from 'recruiter/graphql/candidates.gql'
import { GetCompanyOffices } from 'recruiter/graphql/companies.gql'
import DrawerComponent from 'shared/components/DrawerComponent'
import useToast from 'shared/components/Toast/useToast'
import { CandidateRecruitingStatuses } from 'shared/models/candidateRecruitingStatus'
import { toMonthDate } from 'shared/utils/dateFormatter'
import { getPxAsRem } from 'shared/utils'
import { formatGraphQLError } from 'shared/utils/graphQL'
import { isValidLinkedInUrl } from 'shared/utils/validation'
import * as Yup from 'yup'

import type {
    AddCandidateMutationVariables,
    AddWorkExperienceInput,
    CandidateProfileEntry,
    Colleges,
    DegreeTypes,
    EducationEntry,
    GetCandidateEducationOptionsQueryVariables,
    GetCandidatesByRecentlyViewedQuery,
    Industries,
    Majors,
    MbaColleges,
    OfficeLocations,
    Sectors,
    Titles,
} from '__generated__/graphql'
import {
    GetCandidateWorkExperienceOptions,
    GetCompaniesById,
} from 'candidate/graphql/generatedProfile.gql'
import type { FC, SyntheticEvent } from 'react'
import CompaniesAutocomplete from 'shared/components/CompaniesAutocomplete/CompaniesAutocomplete'
import type { DropdownOption } from 'shared/models/dropdownOption'
import type { ApolloQueryRefetchFunction } from 'shared/types/graphql'
import BulkCandidateDocumentUpload from './BulkCandidateUploadDocuments'

/** The types of education from which a recruiter can select. This dictates whether a major (for undergrads) or degree (for grad students) is required. */
export const educationTypeOptions = ['Undergraduate', 'Graduate'] as const

/** A convenience type for recruiter-selectable education type options. */
type EducationType = (typeof educationTypeOptions)[number]

/** The values of the form for adding a candidate. */
interface AddCandidateFormValues {
    firstName: CandidateProfileEntry['firstName']
    lastName: CandidateProfileEntry['lastName']
    personalEmail: CandidateProfileEntry['email']
    preferredName: CandidateProfileEntry['nickname']
    personalCell: CandidateProfileEntry['phone']
    linkedinUrl: CandidateProfileEntry['linkedinUrl']
    recruitingStatus: CandidateProfileEntry['recruitingStatus']
    preferredEmail: 'personal' | 'work' | null
    workCell: AddWorkExperienceInput['workPhone'] | undefined
    workEmail: AddWorkExperienceInput['workEmail'] | undefined
    companyId: AddWorkExperienceInput['companyId'] | undefined
    companyOfficeId: AddWorkExperienceInput['companyOfficeId'] | undefined
    titleId: AddWorkExperienceInput['titleId'] | undefined
    startDate: AddWorkExperienceInput['startDate'] | undefined
    industryIds: AddWorkExperienceInput['industryIds'] | undefined
    sectorIds: AddWorkExperienceInput['sectorIds'] | undefined
    educationType?: EducationType
    degreeIds: EducationEntry['degreeIds']
    collegeId?: EducationEntry['collegeId']
    graduationDate: EducationEntry['graduationDate']
    gpa: EducationEntry['gpa']
    gpaScale: EducationEntry['gpaScale']
    sendInvitationEmail?: boolean
}

interface AddCandidateFormProps {
    open: boolean
    onClose: () => void
    refetch?: ApolloQueryRefetchFunction<
        GetCandidatesByRecentlyViewedQuery,
        GetCandidateEducationOptionsQueryVariables
    >
}

const AddCandidateForm: FC<AddCandidateFormProps> = ({
    open,
    onClose,
    refetch,
}) => {
    const [tabValue, setTabValue] = useState('single')
    const [industries, setIndustries] = useState<DropdownOption<Industries>[]>(
        []
    )
    const [sectors, setSectors] = useState<DropdownOption<Sectors>[]>([])

    const [colleges, setColleges] = useState<DropdownOption<Colleges>[]>([])
    const [mbaColleges, setMbaColleges] = useState<
        DropdownOption<MbaColleges>[]
    >([])
    const [institutions, setInstitutions] = useState<
        DropdownOption<Colleges | MbaColleges>[]
    >([])
    const [officeLocations, setOfficeLocations] = useState<
        DropdownOption<OfficeLocations>[]
    >([])
    const [titles, setTitles] = useState<DropdownOption<Titles>[]>([])
    const [undergradDegrees, setUndergradDegrees] = useState<
        DropdownOption<Majors>[]
    >([])
    const [graduateDegrees, setGraduateDegrees] = useState<
        DropdownOption<DegreeTypes>[]
    >([])

    const toast = useToast()
    const [addCandidate] = useMutation(AddCandidate)

    useQuery(GetCandidateWorkExperienceOptions, {
        onCompleted: (data) => {
            setIndustries(data.industries)
            setTitles(data.titles)
            setSectors(data.sectors)
        },
        onError: (error) => {
            console.log(
                'Error fetching candidate work experience options',
                error
            )
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(
                    error,
                    'Failed to fetch candidate work experience options'
                ),
            })
        },
    })

    useQuery(GetCandidateEducationOptions, {
        onCompleted: (data) => {
            setColleges(data.colleges)
            setMbaColleges(data.mbaColleges)
            setInstitutions([...data.colleges, ...data.mbaColleges])
            setUndergradDegrees(data.majors)
            setGraduateDegrees(data.degreeTypes)
        },
        onError: (error) => {
            console.log('Error fetching education options', error)
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(
                    error,
                    'Failed to fetch candidate education options'
                ),
            })
        },
    })

    const [getCompanyOffices, { loading: isOfficeLocationDataLoading }] =
        useLazyQuery(GetCompanyOffices, {
            onCompleted: (data) => {
                const companyOfficeChoices =
                    data.company.companyOffices?.map(
                        ({ officeLocation, id }) => ({
                            id,
                            value: officeLocation?.value ?? '',
                        })
                    ) ?? []
                setOfficeLocations(companyOfficeChoices)
            },
        })

    const validationSchema: Yup.ObjectSchema<AddCandidateFormValues> =
        Yup.object({
            // Personal Information
            firstName: Yup.string().required('First name is required'),
            lastName: Yup.string().required('Last name is required'),
            preferredName: Yup.string().optional(),
            linkedinUrl: Yup.string()
                .test('isValidLinkedInUrl', 'Invalid LinkedIn URL', (value) => {
                    if (!value) return true // Allow empty URLs (optional field)
                    return isValidLinkedInUrl(value)
                })
                .optional(),
            recruitingStatus: Yup.string().optional(),

            // Contact Information
            personalCell: Yup.string().optional(),
            workCell: Yup.string().optional(),
            personalEmail: Yup.string()
                .required('Personal email is required')
                .email('Invalid email'),
            workEmail: Yup.string()
                .email('Invalid work email')
                .when('preferredEmail', {
                    is: (val: string) => val === 'work',
                    then: (schema) =>
                        schema.nonNullable().required('Work email is required'),
                }),
            preferredEmail: Yup.string()
                .oneOf(['personal', 'work'])
                .required('Please select a preferred email type'),

            // Work Experience
            companyId: Yup.string().when(['workEmail', 'workCell'], {
                is: (workEmail, workCell) =>
                    Boolean(workEmail) || Boolean(workCell),
                then: (schema) =>
                    schema.required(
                        'Company ID is required when work email or work cell is provided'
                    ),
                otherwise: (schema) => schema.optional(),
            }),
            titleId: Yup.string().optional(),
            companyOfficeId: Yup.string().optional(),
            startDate: Yup.string().optional(),
            industryIds: Yup.array()
                .of(Yup.string().required())
                .when('companyId', {
                    is: (companyId) => Boolean(companyId),
                    then: (schema) =>
                        schema.min(
                            1,
                            'Industry is required when providing work experience'
                        ),
                }),
            sectorIds: Yup.array().of(Yup.string().required()).optional(),

            // Education
            educationType: Yup.string<EducationType>(),
            collegeId: Yup.string().when('educationType', {
                is: (educationType) => Boolean(educationType),
                then: (schema) =>
                    schema.required(
                        'College is required when providing education'
                    ),
            }),
            degreeIds: Yup.array()
                .of(Yup.string().required('Major/Degree is required'))
                .required('Major/Degree is required'),
            graduationDate: Yup.string().when('educationType', {
                is: (educationType) => Boolean(educationType),
                then: (schema) =>
                    schema.required(
                        'Graduation date is required when providing education'
                    ),
            }),
            gpa: Yup.number()
                .min(0)
                .max(5)
                .max(Yup.ref('gpaScale'), 'GPA cannot exceed GPA scale'),
            gpaScale: Yup.number(),

            sendInvitationEmail: Yup.boolean(),
        })

    const formik = useFormik<AddCandidateFormValues>({
        initialValues: {
            firstName: '',
            lastName: '',
            preferredName: undefined,
            linkedinUrl: undefined,
            recruitingStatus: undefined,
            personalCell: undefined,
            workCell: undefined,
            personalEmail: '',
            workEmail: undefined,
            preferredEmail: 'personal',
            companyId: '',
            companyOfficeId: undefined,
            titleId: '',
            startDate: undefined,
            industryIds: [],
            sectorIds: [],
            educationType: undefined,
            degreeIds: [],
            collegeId: '',
            graduationDate: undefined,
            gpa: undefined,
            gpaScale: undefined,
            sendInvitationEmail: false,
        },
        validationSchema,
        onSubmit: (values) => {
            handleAddCandidate(values)
        },
    })

    useEffect(() => {
        if (!formik.values.companyId) return

        getCompanyOffices({
            variables: { companyId: formik.values.companyId },
        })
    }, [formik.values.companyId, getCompanyOffices])

    const handleAddCandidate = (values: AddCandidateFormValues) => {
        const variables: AddCandidateMutationVariables = {
            profile: {
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.personalEmail,
                nickname: values.preferredName || undefined,
                linkedinUrl: values.linkedinUrl || undefined,
                recruitingStatus: values.recruitingStatus || undefined,
                phone: values.personalCell || undefined,
            },
            sendInvitationEmail: values.sendInvitationEmail,
            prefersWorkEmail: values.preferredEmail === 'work',
        }

        if (values.companyId) {
            variables.workExperience ??= {
                companyId: values.companyId,
                companyOfficeId: values.companyOfficeId,
                industryIds: values.industryIds,
                sectorIds: values.sectorIds,
                startDate: values.startDate || '',
                titleId: values.titleId || '',
                workEmail: values.workEmail,
                workPhone: values.workCell,
                isCurrent: true,
            }
        }

        if (values.educationType && values.collegeId) {
            variables.education = {
                collegeId: values.collegeId,
                degreeIds: values.degreeIds,
                graduationDate: values.graduationDate,
                gpa: values.gpa ? Number(values.gpa) : undefined,
                gpaScale: values.gpaScale ? Number(values.gpaScale) : undefined,
            }
        }

        addCandidate({
            variables,
            onCompleted: (data) => {
                if (data.addCandidate.success) {
                    toast({
                        title: 'Success!',
                        message: 'Candidate has been added successfully',
                        severity: 'success',
                    })
                    refetch?.()
                    handleCancel()
                } else {
                    toast({
                        title: 'Error!',
                        message: `Failed to add candidate: ${data.addCandidate.message}`,
                        severity: 'error',
                    })
                }
            },
            onError: (error) => {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message: 'Failed to add candidate, please try again later',
                })
            },
        })
    }

    const handleCancel = () => {
        formik.resetForm()
        onClose()
    }

    const handleTabChange = (event: SyntheticEvent, newValue: string) => {
        setTabValue(newValue)
    }

    const handleDateChange = (name, value) => {
        formik.setFieldValue(name, value)
    }

    // Generate a set of GPA options from 5.0 to 2.0 in increments of 0.1.
    const gpaOptions = useMemo(() => {
        const gpaOptions: DropdownOption[] = []
        const gpaMaximum = 5.0
        const gpaMinimum = 2.0
        const gpaValueStep = 0.1

        for (let i = gpaMaximum; i >= gpaMinimum; i -= gpaValueStep) {
            gpaOptions.push({
                id: i.toFixed(1),
                value: i.toFixed(1),
            })
        }

        gpaOptions.push({ id: '0.0', value: 'N/A' })

        return gpaOptions
    }, [])

    const gpaScaleOptions: DropdownOption[] = [
        { id: '4.0', value: '4.0' },
        { id: '4.3', value: '4.3' },
        { id: '5.0', value: '5.0' },
    ]

    const mbaDegreeId = useMemo(
        () => graduateDegrees.find((major) => major.value === 'MBA')?.id,
        [graduateDegrees]
    )

    const { data: companiesById } = useQuery(GetCompaniesById, {
        variables: {
            ids: formik.values.companyId ? [formik.values.companyId] : [],
        },
        skip: !formik.values.companyId,
    })

    const getDegreesValue = (
        degreeIds: AddCandidateFormValues['degreeIds']
    ) => {
        const educationType = formik.values.educationType
        const isUndergraduate = educationType === 'Undergraduate'
        const degreeOptions = isUndergraduate
            ? undergradDegrees
            : graduateDegrees

        const values = degreeOptions.filter(({ id }) => degreeIds?.includes(id))
        return values
    }

    return (
        <DrawerComponent
            open={open}
            onClose={handleCancel}
            headerTitle="Add Candidate"
            hasSaveButton={false}
            width={getPxAsRem(800)}
        >
            {/* Tabs for SINGLE and BULK */}
            <TabContext value={tabValue}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="Single and Bulk"
                    variant="fullWidth"
                    sx={{ mb: 2 }}
                >
                    <Tab label="SINGLE" value="single" />
                    <Tab label="BULK" value="bulk" />
                </Tabs>

                {/* TabPanel for SINGLE */}
                <TabPanel value="single">
                    <Box component="form">
                        {/* Personal Information */}
                        <Typography variant="h5" gutterBottom>
                            Personal Information
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="First Name"
                                    name="firstName"
                                    value={formik.values.firstName}
                                    onChange={formik.handleChange}
                                    error={
                                        formik.touched.firstName &&
                                        Boolean(formik.errors.firstName)
                                    }
                                    helperText={
                                        formik.touched.firstName &&
                                        formik.errors.firstName
                                    }
                                    required
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Last Name"
                                    name="lastName"
                                    value={formik.values.lastName}
                                    onChange={formik.handleChange}
                                    error={
                                        formik.touched.lastName &&
                                        Boolean(formik.errors.lastName)
                                    }
                                    helperText={
                                        formik.touched.lastName &&
                                        formik.errors.lastName
                                    }
                                    required
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Preferred Name"
                                    name="preferredName"
                                    value={formik.values.preferredName ?? ''}
                                    onChange={formik.handleChange}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="LinkedIn URI"
                                    name="linkedinUrl"
                                    value={formik.values.linkedinUrl ?? ''}
                                    onChange={formik.handleChange}
                                    error={
                                        formik.touched.linkedinUrl &&
                                        Boolean(formik.errors.linkedinUrl)
                                    }
                                    helperText={
                                        formik.touched.linkedinUrl &&
                                        formik.errors.linkedinUrl
                                    }
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Autocomplete
                                    fullWidth
                                    options={CandidateRecruitingStatuses}
                                    getOptionLabel={(option) => option}
                                    value={
                                        formik.values.recruitingStatus ?? null
                                    }
                                    onChange={(event, newValue) => {
                                        formik.setFieldValue(
                                            'recruitingStatus',
                                            newValue ?? undefined
                                        )
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Recruiting Status"
                                            variant="outlined"
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>

                        {/* Contact Information */}
                        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
                            Contact Information
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Personal Cell Phone"
                                    name="personalCell"
                                    value={formik.values.personalCell ?? ''}
                                    onChange={formik.handleChange}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Work Cell Phone"
                                    name="workCell"
                                    value={formik.values.workCell ?? ''}
                                    onChange={formik.handleChange}
                                />
                            </Grid>

                            <Grid
                                container
                                item
                                xs={12}
                                columnSpacing={2}
                                rowSpacing={0}
                            >
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Personal Email"
                                        name="personalEmail"
                                        value={formik.values.personalEmail}
                                        onChange={formik.handleChange}
                                        error={
                                            formik.touched.personalEmail &&
                                            Boolean(formik.errors.personalEmail)
                                        }
                                        helperText={
                                            formik.touched.personalEmail &&
                                            formik.errors.personalEmail
                                        }
                                        required
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Work Email"
                                        name="workEmail"
                                        value={formik.values.workEmail ?? ''}
                                        onChange={formik.handleChange}
                                        error={
                                            formik.touched.workEmail &&
                                            Boolean(formik.errors.workEmail)
                                        }
                                        helperText={
                                            formik.touched.workEmail &&
                                            formik.errors.workEmail
                                        }
                                        required={
                                            formik.values.preferredEmail ===
                                            'work'
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <FormLabel
                                            id="add-candidate-preferred-email-radio-label"
                                            style={visuallyHidden}
                                        >
                                            Preferred Email
                                        </FormLabel>
                                        <RadioGroup
                                            aria-labelledby="add-candidate-preferred-email-radio-label"
                                            name="add-candidate-preferred-email-radio"
                                            onChange={(e, newValue) =>
                                                formik.setFieldValue(
                                                    'preferredEmail',
                                                    newValue
                                                )
                                            }
                                            row
                                            sx={{ gap: 2 }}
                                            value={formik.values.preferredEmail}
                                        >
                                            <FormControlLabel
                                                control={<Radio />}
                                                label="Prefers Personal Email"
                                                value="personal"
                                                sx={{ flex: 1 }}
                                            />
                                            <FormControlLabel
                                                control={<Radio />}
                                                label="Prefers Work Email"
                                                value="work"
                                                sx={{ flex: 1 }}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                {formik.touched.preferredEmail &&
                                    formik.errors.preferredEmail && (
                                        <Typography
                                            color="error"
                                            variant="body2"
                                        >
                                            {formik.errors.preferredEmail}
                                        </Typography>
                                    )}
                            </Grid>
                        </Grid>

                        {/* Work Experience */}
                        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
                            Work Experience
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <CompaniesAutocomplete
                                    fullWidth
                                    getOptionLabel={(option) => option.value}
                                    value={
                                        companiesById?.companiesById[0] ?? null
                                    }
                                    onChange={(event, newValue) => {
                                        formik.setFieldValue(
                                            'companyId',
                                            newValue?.id
                                        )
                                    }}
                                    error={
                                        formik.touched.companyId &&
                                        Boolean(formik.errors.companyId)
                                    }
                                    helperText={
                                        formik.touched.companyId
                                            ? formik.errors.companyId
                                            : ''
                                    }
                                    label="Company"
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <Autocomplete
                                    fullWidth
                                    disabled={!formik.values.companyId}
                                    options={titles}
                                    getOptionLabel={(option) => option.value}
                                    value={
                                        titles.find(
                                            (title) =>
                                                title.id ===
                                                formik.values.titleId
                                        ) ?? null
                                    }
                                    onChange={(event, newValue) => {
                                        formik.setFieldValue(
                                            'titleId',
                                            newValue?.id
                                        )
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Title"
                                            variant="outlined"
                                            required={Boolean(
                                                formik.values.companyId
                                            )}
                                            error={
                                                formik.touched.titleId &&
                                                Boolean(formik.errors.titleId)
                                            }
                                            helperText={
                                                formik.touched.titleId &&
                                                formik.errors.titleId
                                            }
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Autocomplete
                                    fullWidth
                                    options={officeLocations}
                                    getOptionLabel={(option) => option.value}
                                    value={
                                        officeLocations.find(
                                            (location) =>
                                                location.id ===
                                                formik.values.companyOfficeId
                                        ) ?? null
                                    }
                                    onChange={(event, newValue) => {
                                        formik.setFieldValue(
                                            'companyOfficeId',
                                            newValue?.id
                                        )
                                    }}
                                    disabled={
                                        isOfficeLocationDataLoading ||
                                        !formik.values.companyId
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Location"
                                            variant="outlined"
                                            error={
                                                formik.touched
                                                    .companyOfficeId &&
                                                Boolean(
                                                    formik.errors
                                                        .companyOfficeId
                                                )
                                            }
                                            helperText={
                                                formik.touched
                                                    .companyOfficeId &&
                                                formik.errors.companyOfficeId
                                            }
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <DatePicker
                                        label="Start Date"
                                        value={toMonthDate(
                                            formik.values.startDate,
                                            'dateobject'
                                        )}
                                        format="MM/yyyy"
                                        onChange={(date) => {
                                            const startDate =
                                                date && isValid(date)
                                                    ? format(date, 'MM-yyyy')
                                                    : undefined
                                            handleDateChange(
                                                'startDate',
                                                startDate
                                            )
                                        }}
                                        views={['year', 'month']}
                                        disabled={!formik.values.companyId}
                                        slotProps={{
                                            textField: {
                                                helperText:
                                                    formik.touched.startDate &&
                                                    formik.errors.startDate,
                                                error:
                                                    formik.touched.startDate &&
                                                    Boolean(
                                                        formik.errors.startDate
                                                    ),
                                            },
                                        }}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <Autocomplete
                                    fullWidth
                                    multiple
                                    options={industries}
                                    getOptionLabel={(option) => option.value}
                                    value={
                                        industries.filter((industry) =>
                                            formik.values.industryIds?.includes(
                                                industry.id
                                            )
                                        ) ?? []
                                    }
                                    onChange={(event, newValue) => {
                                        formik.setFieldValue(
                                            'industryIds',
                                            newValue?.map(
                                                (industry) => industry.id
                                            )
                                        )
                                    }}
                                    disabled={!formik.values.companyId}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Industry"
                                            variant="outlined"
                                            required={Boolean(
                                                formik.values.companyId
                                            )}
                                            error={
                                                formik.touched.industryIds &&
                                                Boolean(
                                                    formik.errors.industryIds
                                                )
                                            }
                                            helperText={
                                                formik.touched.industryIds &&
                                                formik.errors.industryIds
                                            }
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Autocomplete
                                    fullWidth
                                    multiple
                                    options={sectors}
                                    getOptionLabel={(option) => option.value}
                                    value={
                                        sectors.filter((sector) =>
                                            formik.values.sectorIds?.includes(
                                                sector.id
                                            )
                                        ) ?? []
                                    }
                                    onChange={(event, newValue) => {
                                        formik.setFieldValue(
                                            'sectorIds',
                                            newValue.map((sector) => sector.id)
                                        )
                                    }}
                                    disabled={!formik.values.companyId}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Sector"
                                            variant="outlined"
                                            error={
                                                formik.touched.sectorIds &&
                                                Boolean(formik.errors.sectorIds)
                                            }
                                            helperText={
                                                formik.touched.sectorIds &&
                                                formik.errors.sectorIds
                                            }
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>

                        {/* Education */}
                        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
                            Education
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Autocomplete
                                    fullWidth
                                    options={educationTypeOptions}
                                    getOptionLabel={(option) => option}
                                    value={formik.values.educationType ?? null}
                                    onChange={(event, newValue) => {
                                        // Clear the degree and college fields when switching between education types.
                                        formik.setFieldValue(
                                            'degreeType',
                                            undefined
                                        )

                                        formik.setFieldValue(
                                            'collegeId',
                                            undefined
                                        )

                                        formik.setFieldValue('degreeIds', [])

                                        formik.setFieldValue(
                                            'educationType',
                                            newValue ?? undefined
                                        )
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Education Type"
                                            variant="outlined"
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Autocomplete
                                    fullWidth
                                    multiple
                                    options={
                                        formik.values.educationType ===
                                        'Undergraduate'
                                            ? undergradDegrees
                                            : graduateDegrees
                                    }
                                    getOptionLabel={(option) => option.value}
                                    getOptionDisabled={(option) =>
                                        formik.values.educationType ===
                                            'Graduate' &&
                                        (formik.values.degreeIds?.length ||
                                            0) >= 1
                                    }
                                    value={getDegreesValue(
                                        formik.values.degreeIds
                                    )}
                                    onChange={(event, newValue) => {
                                        // Clear the college field when switching graduate degree types.
                                        formik.setFieldValue(
                                            'collegeId',
                                            undefined
                                        )
                                        formik.setFieldValue(
                                            'degreeIds',
                                            newValue.map((degree) => degree.id)
                                        )
                                    }}
                                    disabled={!formik.values.educationType}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={
                                                formik.values.educationType ===
                                                'Undergraduate'
                                                    ? 'Major'
                                                    : 'Degree Type'
                                            }
                                            variant="outlined"
                                            error={
                                                formik.touched.degreeIds &&
                                                Boolean(formik.errors.degreeIds)
                                            }
                                            helperText={
                                                formik.touched.degreeIds &&
                                                formik.errors.degreeIds
                                            }
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Autocomplete
                                    fullWidth
                                    options={
                                        formik.values.educationType ===
                                            'Graduate' &&
                                        formik.values.degreeIds?.includes(
                                            mbaDegreeId || ''
                                        )
                                            ? mbaColleges
                                            : colleges
                                    }
                                    getOptionLabel={(option) => option.value}
                                    value={
                                        institutions.find(
                                            (college) =>
                                                college.id ===
                                                formik.values.collegeId
                                        ) ?? null
                                    }
                                    onChange={(event, newValue) => {
                                        formik.setFieldValue(
                                            'collegeId',
                                            newValue?.id
                                        )
                                    }}
                                    disabled={
                                        !formik.values.educationType ||
                                        (formik.values.educationType ===
                                            'Graduate' &&
                                            !formik.values.degreeIds)
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="College"
                                            variant="outlined"
                                            required={Boolean(
                                                formik.values.educationType
                                            )}
                                            error={
                                                formik.touched.collegeId &&
                                                Boolean(formik.errors.collegeId)
                                            }
                                            helperText={
                                                formik.touched.collegeId &&
                                                formik.errors.collegeId
                                            }
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <DatePicker
                                        label="Graduation Date"
                                        value={toMonthDate(
                                            formik.values.graduationDate,
                                            'dateobject'
                                        )}
                                        format="MM/yyyy"
                                        onChange={(date: Date | null) => {
                                            const gradDate =
                                                date && isValid(date)
                                                    ? format(date, 'MM-yyyy')
                                                    : ''
                                            handleDateChange(
                                                'graduationDate',
                                                gradDate
                                            )
                                        }}
                                        views={['year', 'month']}
                                        disabled={!formik.values.educationType}
                                        slotProps={{
                                            textField: {
                                                helperText:
                                                    formik.touched
                                                        .graduationDate &&
                                                    formik.errors
                                                        .graduationDate,
                                                required: Boolean(
                                                    formik.values.educationType
                                                ),
                                                error:
                                                    formik.touched
                                                        .graduationDate &&
                                                    Boolean(
                                                        formik.errors
                                                            .graduationDate
                                                    ),
                                            },
                                        }}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <Autocomplete
                                    fullWidth
                                    options={gpaOptions}
                                    getOptionLabel={(option) => option.value}
                                    value={
                                        gpaOptions.find(
                                            (gpa) =>
                                                gpa.id ===
                                                formik.values.gpa?.toString()
                                        ) ?? null
                                    }
                                    onChange={(event, newValue) => {
                                        formik.setFieldValue(
                                            'gpa',
                                            newValue?.id
                                        )
                                    }}
                                    disabled={!formik.values.educationType}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Cumulative GPA"
                                            variant="outlined"
                                            error={
                                                formik.touched.gpa &&
                                                Boolean(formik.errors.gpa)
                                            }
                                            helperText={
                                                formik.touched.gpa &&
                                                formik.errors.gpa
                                            }
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Autocomplete
                                    fullWidth
                                    options={gpaScaleOptions}
                                    getOptionLabel={(option) => option.value}
                                    value={
                                        gpaScaleOptions.find(
                                            (gpaScale) =>
                                                gpaScale.id ===
                                                formik.values.gpaScale?.toString()
                                        ) ?? null
                                    }
                                    onChange={(event, newValue) => {
                                        formik.setFieldValue(
                                            'gpaScale',
                                            newValue?.id
                                        )
                                    }}
                                    disabled={!formik.values.educationType}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="GPA Scale"
                                            variant="outlined"
                                            error={
                                                formik.touched.gpaScale &&
                                                Boolean(formik.errors.gpaScale)
                                            }
                                            helperText={
                                                formik.touched.gpaScale &&
                                                formik.errors.gpaScale
                                            }
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>

                        {/* Footer */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formik.values.sendInvitationEmail}
                                    onChange={formik.handleChange}
                                    name="sendInvitationEmail"
                                />
                            }
                            label="Send Invitation Email"
                            sx={{ mb: getPxAsRem(20) }}
                        />
                        <Box sx={{ display: 'flex', gap: getPxAsRem(12) }}>
                            <Button
                                sx={{ width: '50%' }}
                                variant="outlined"
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                sx={{ width: '50%' }}
                                onClick={() => {
                                    formik.handleSubmit()
                                }}
                            >
                                Save
                            </Button>
                        </Box>
                    </Box>
                </TabPanel>
                {/* TabPanel for BULK */}
                <TabPanel value="bulk">
                    <Box>
                        <BulkCandidateDocumentUpload />
                    </Box>
                </TabPanel>
            </TabContext>
        </DrawerComponent>
    )
}

export default AddCandidateForm
